import React, { useMemo, useState } from 'react';
import { FunnelStage, Lead, Contact, Tag } from '../../../types';
import KanbanCard from './KanbanCard';
import { PlusIcon } from '../../../components/icons';
import { useTasks } from '../../../contexts/tasks/TasksContext';
import { cn } from '@/lib/utils';

const STAGE_DOT = ['kanban-column__dot--info', 'kanban-column__dot--warning', 'kanban-column__dot--brand', 'kanban-column__dot--success'] as const;
const STAGE_ACCENT_COLORS = ['var(--info-600)', 'var(--warning-600)', 'var(--brand-900)', 'var(--success-600)'] as const;

interface KanbanColumnProps {
  stage: FunnelStage;
  index: number;
  leads: Lead[];
  onCardClick: (lead: Lead) => void;
  onDrop: (leadId: string, stageId: string) => void;
  onCreateLead?: (stageId: string) => void;
  onChatClick?: (lead: Lead) => void;
  contacts: Contact[];
  tags: Tag[];
  dropDisabled?: boolean;
}

const KanbanColumn = ({
  stage, index, leads, onCardClick, onDrop, onCreateLead, onChatClick, contacts, tags, dropDisabled = false,
}: KanbanColumnProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const { tasks } = useTasks();
  const stageAccentColor = stage.color || STAGE_ACCENT_COLORS[index % STAGE_ACCENT_COLORS.length];

  const contactsMap = useMemo(() => new Map(contacts.map(c => [c.id, c])), [contacts]);
  const tagsMap = useMemo(() => new Map(tags.map(t => [t.id, t])), [tags]);
  const tasksByLeadId = useMemo(() => {
    const map = new Map<string, typeof tasks>();
    for (const task of tasks) {
      const list = map.get(task.leadId);
      if (list) list.push(task);
      else map.set(task.leadId, [task]);
    }
    return map;
  }, [tasks]);

  const totalValue = new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(leads.reduce((sum, lead) => sum + (lead.value || 0), 0));

  return (
    <section
      className={cn(
        'kanban-column h-full w-full',
        'max-md:max-h-[calc(100dvh-14rem)]',
        'md:h-auto md:w-[280px] md:min-w-[280px]',
        isDragOver && 'ring-2 ring-brand-200 rounded-lg'
      )}
      aria-label={`Etapa ${stage.name}`}
      onDragOver={(e) => { if (!dropDisabled) { e.preventDefault(); setIsDragOver(true); } }}
      onDrop={(e) => {
        if (dropDisabled) return;
        e.preventDefault();
        const leadId = e.dataTransfer.getData('leadId');
        if (leadId) onDrop(leadId, stage.id);
        setIsDragOver(false);
      }}
      onDragLeave={() => setIsDragOver(false)}
    >
      <header className="kanban-column__header">
        <span className={`kanban-column__dot ${STAGE_DOT[index % STAGE_DOT.length]}`} aria-hidden />
        <h3 className="kanban-column__title truncate">{stage.name}</h3>
        <span className="kanban-column__count" aria-label={`${leads.length} negociações`}>{leads.length}</span>
        <span className="kanban-column__total">{totalValue}</span>
      </header>

      <ul className="kanban-column__list m-0 list-none space-y-s2 p-0">
        {leads.map(lead => (
          <KanbanCard
            key={lead.id}
            lead={lead}
            contactName={lead.contactId ? contactsMap.get(lead.contactId)?.name : undefined}
            tasks={tasksByLeadId.get(lead.id) ?? []}
            tags={(lead.tags ?? []).flatMap(tagId => { const tag = tagsMap.get(tagId); return tag ? [tag] : []; })}
            stageAccentColor={stageAccentColor}
            onClick={() => onCardClick(lead)}
            onChatClick={onChatClick}
          />
        ))}
      </ul>

      {onCreateLead && (
        <button
          type="button"
          onClick={() => onCreateLead(stage.id)}
          className="kanban-add mx-s1 mb-s1"
          aria-label="Criar negociação"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Criar negociação</span>
        </button>
      )}
    </section>
  );
};

export default React.memo(KanbanColumn);
